import { ClientTable } from "../../../components/ui/ClientTable";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { ContentCard } from "../../../components/ui";
import { InventoryActionToast } from "../components/InventoryActionToast";
import { RequestRevokeModal } from "../components/RequestRevokeModal";
import { MarkAsDeliveredModal } from "../components/MarkAsDeliveredModal";
import { inventoryManagementService } from "../../../services/inventoryManagementService";
import { cn } from "../../../lib/utils";
import {
  allocationStatusClass,
  allocationStatusLabel,
  canActOnAllocationStatus,
  isDeliveredAllocationStatus,
  isPartiallyAllocatedHeaderStatus,
  isPendingApprovalStatus,
  isUndeliverablePartialLineStatus,
} from "../utils/allocationStatus";

const MONTH_KEYS = [
  "dateTime.monthJan",
  "dateTime.monthFeb",
  "dateTime.monthMar",
  "dateTime.monthApr",
  "dateTime.monthMay",
  "dateTime.monthJun",
  "dateTime.monthJul",
  "dateTime.monthAug",
  "dateTime.monthSep",
  "dateTime.monthOct",
  "dateTime.monthNov",
  "dateTime.monthDec",
];

function formatCurrency(val) {
  if (val === null || val === undefined || val === "") return "—";
  const n = Number(val);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function isRevocableBatch(batch, pendingRevokeDetailIds) {
  if (!batch?.customerAllocationLineDetailId) return false;
  if (pendingRevokeDetailIds.has(String(batch.customerAllocationLineDetailId))) {
    return false;
  }
  if (isDeliveredAllocationStatus(batch.status)) return false;
  if (isPendingApprovalStatus(batch.status)) return false;
  return true;
}

export function AllocationDetailPage() {
  const { t } = useTranslation();
  const { allocationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successToast, setSuccessToast] = useState("");
  const [allocation, setAllocation] = useState(null);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [deliverModalOpen, setDeliverModalOpen] = useState(false);
  const [actionError, setActionError] = useState("");
  const [pendingRevokeDetailIds, setPendingRevokeDetailIds] = useState(
    () => new Set()
  );

  const loadAllocation = useCallback(() => {
    let active = true;
    setLoading(true);
    setError("");
    setActionError("");

    Promise.allSettled([
      inventoryManagementService.getAllocationDetail(allocationId),
      inventoryManagementService.getPendingRevokeUnlockRequests(),
    ])
      .then(([detailResult, pendingResult]) => {
        if (!active) return;
        if (detailResult.status === "rejected") {
          throw detailResult.reason;
        }
        const res = detailResult.value;
        setAllocation(res);

        if (pendingResult.status === "fulfilled") {
          const allocationKey = String(
            res?.header?.allocationId || allocationId || ""
          );
          const pendingIds = new Set(
            (pendingResult.value || [])
              .filter((req) => {
                if (String(req.status || "").toUpperCase() !== "PENDING") {
                  return false;
                }
                if (!allocationKey) return true;
                return String(req.customerAllocationId || "") === allocationKey;
              })
              .map((req) => String(req.customerAllocationDetailId || ""))
              .filter(Boolean)
          );
          setPendingRevokeDetailIds(pendingIds);
        } else {
          console.error(
            "Failed to load pending revoke requests:",
            pendingResult.reason
          );
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load allocation detail:", err);
        setError(err?.message || t("validation.allocationNotFound"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [allocationId, t]);

  useEffect(() => {
    const cleanup = loadAllocation();
    return cleanup;
  }, [loadAllocation]);

  const resolvedStatus = useMemo(() => {
    if (!allocation) return "";
    return allocation.header.status || "Allocated";
  }, [allocation]);

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "—") return "—";
    const dateObj = new Date(dateStr);
    if (!Number.isFinite(dateObj.getTime())) return String(dateStr);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = t(MONTH_KEYS[dateObj.getMonth()]);
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleBack = () => {
    navigate("/inventory-management/customer-allocations");
  };

  const deliverBatchLabel = (product) => {
    const batches = (product?.batches || [])
      .map((b) => b.batchNumber)
      .filter((n) => n && n !== "—");
    return batches.length > 0 ? batches.join(", ") : "—";
  };

  const canMarkLineDelivered = useCallback(
    (product) => {
      if (!product) return false;
      if (!canActOnAllocationStatus(resolvedStatus)) return false;
      if (isDeliveredAllocationStatus(product.status, product.deliveryDate)) {
        return false;
      }
      if (
        isPartiallyAllocatedHeaderStatus(resolvedStatus) &&
        isUndeliverablePartialLineStatus(product.status)
      ) {
        return false;
      }
      const allocated = Number(product.allocatedQty) || 0;
      return allocated > 0;
    },
    [resolvedStatus]
  );

  const deliverableProducts = useMemo(() => {
    if (!allocation?.products) return [];
    return allocation.products
      .filter((product) => canMarkLineDelivered(product))
      .map((product) => ({
        id: String(product.customerAllocationLineId || product.id),
        name: product.name,
        batchNumber: deliverBatchLabel(product),
        allocatedQty: product.allocatedQty,
        invoiceNumber:
          (product.invoiceNumber && product.invoiceNumber !== "—"
            ? String(product.invoiceNumber)
            : "") ||
          (allocation.header?.invoiceNumber &&
          allocation.header.invoiceNumber !== "—"
            ? String(allocation.header.invoiceNumber)
            : ""),
        deliveryNotes: product.deliveryNotes || "",
      }));
  }, [allocation, canMarkLineDelivered]);

  const revocableProducts = useMemo(() => {
    if (!allocation?.products) return [];
    if (!canActOnAllocationStatus(resolvedStatus)) return [];
    return allocation.products
      .filter((product) => {
        if (isDeliveredAllocationStatus(product.status, product.deliveryDate)) {
          return false;
        }
        return (product.batches || []).some((b) =>
          isRevocableBatch(b, pendingRevokeDetailIds)
        );
      })
      .map((product) => ({
        id: String(product.customerAllocationLineId || product.id),
        name: product.name,
        allocatedQty: product.allocatedQty,
      }));
  }, [allocation, resolvedStatus, pendingRevokeDetailIds]);

  const canRequestRevoke = revocableProducts.length > 0;
  const showHeaderActions =
    deliverableProducts.length > 0 || canRequestRevoke;

  const openMarkAsDelivered = () => {
    setActionError("");
    setDeliverModalOpen(true);
  };

  const handleMarkAsDeliveredConfirm = (deliveries) => {
    if (!allocation || !deliveries?.length) return;
    setSubmitting(true);
    setActionError("");

    inventoryManagementService
      .markAllocationLinesDelivered({
        allocation,
        deliveries,
      })
      .then(async () => {
        setDeliverModalOpen(false);
        setSuccessToast(t("formActions.markAsDeliveredSuccess"));
        setTimeout(() => setSuccessToast(""), 3000);
        try {
          const refreshed = await inventoryManagementService.getAllocationDetail(
            allocationId
          );
          setAllocation(refreshed);
        } catch (refreshErr) {
          console.error("Failed to refresh allocation after delivery:", refreshErr);
        }
        setSubmitting(false);
      })
      .catch((err) => {
        console.error("Failed to mark allocation lines as delivered:", err);
        setActionError(err?.message || t("validation.markAsDeliveredError"));
        setSubmitting(false);
      });
  };

  const handleRevokeSubmit = ({ reason, lineIds = [] } = {}) => {
    const products = (allocation?.products || []).filter((p) => {
      const id = String(p.customerAllocationLineId || p.id);
      if (!lineIds.map(String).includes(id)) return false;
      if (isDeliveredAllocationStatus(p.status, p.deliveryDate)) return false;
      return true;
    });

    const detailPayloads = products.flatMap((product) =>
      (product.batches || [])
        .filter((b) => isRevocableBatch(b, pendingRevokeDetailIds))
        .map((detail) => ({
          customerAllocationDetailId: Number(
            detail.customerAllocationLineDetailId
          ),
          revokeQuantity: Number(detail.allocatedQty) || 0,
        }))
    );

    if (!detailPayloads.length) {
      setActionError(t("inventory.noRevocableProducts"));
      return;
    }

    setSubmitting(true);
    setActionError("");

    Promise.all(
      detailPayloads.map((detail) =>
        inventoryManagementService.createRevokeUnlockRequest({
          customerAllocationDetailId: detail.customerAllocationDetailId,
          actionType: "REVOKE",
          revokeQuantity: detail.revokeQuantity,
          reason,
        })
      )
    )
      .then(() => {
        setPendingRevokeDetailIds((prev) => {
          const next = new Set(prev);
          detailPayloads.forEach((d) =>
            next.add(String(d.customerAllocationDetailId))
          );
          return next;
        });
        setRevokeModalOpen(false);
        setSuccessToast(t("formActions.revokeSubmittedSuccess"));
        setSubmitting(false);
        setTimeout(() => setSuccessToast(""), 3000);
        loadAllocation();
      })
      .catch((err) => {
        console.error("Failed to submit revoke request:", err);
        setActionError(err?.message || t("validation.revokeSubmitError"));
        setSubmitting(false);
      });
  };

  const statusLabel = (status) => allocationStatusLabel(status, t);
  const getStatusBadgeClass = (status) => allocationStatusClass(status);

  const fieldLabelClass =
    "text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1";
  const fieldValueClass = "text-sm font-bold text-foreground";

  return (
    <AppLayout hideBreadcrumb className="space-y-6 animate-fade-in">
      {successToast ? <InventoryActionToast toast={successToast} /> : null}

      <RequestRevokeModal
        open={revokeModalOpen}
        onOpenChange={setRevokeModalOpen}
        referenceCode={allocation?.header.reference}
        products={revocableProducts}
        submitting={submitting}
        submitError={actionError}
        onSubmit={handleRevokeSubmit}
      />

      <MarkAsDeliveredModal
        open={deliverModalOpen}
        onOpenChange={setDeliverModalOpen}
        customerId={allocation?.header?.customerId}
        products={deliverableProducts}
        submitting={submitting}
        onConfirm={handleMarkAsDeliveredConfirm}
      />

      <div className="space-y-1 font-sans">
        {loading ? (
          <PageTitleRow
            back={
              <PageBackLink
                to="/inventory-management/customer-allocations"
                label={t("formActions.back")}
              />
            }
          >
            <div className="h-10 bg-gray-100 rounded w-1/3 animate-pulse" />
          </PageTitleRow>
        ) : error ? (
          <PageTitleRow
            back={
              <PageBackLink
                to="/inventory-management/customer-allocations"
                label={t("formActions.back")}
              />
            }
          >
            <h1 className="text-xl font-extrabold text-foreground tracking-tight">
              {t("inventory.allocationDetail")}
            </h1>
          </PageTitleRow>
        ) : (
          <div className="space-y-1">
            <PageTitleRow
              back={
                <PageBackLink
                  to="/inventory-management/customer-allocations"
                  label={t("formActions.back")}
                />
              }
            >
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-extrabold text-foreground tracking-tight uppercase">
                  {t("inventory.allocationRef", {
                    ref: allocation.header.reference,
                  })}
                </h1>
                {(Array.isArray(allocation.header.lineStatuses) &&
                allocation.header.lineStatuses.length > 0
                  ? allocation.header.lineStatuses
                  : [resolvedStatus].filter(Boolean)
                ).map((status) => (
                  <span
                    key={String(status)}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border",
                      getStatusBadgeClass(status)
                    )}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    {statusLabel(status)}
                  </span>
                ))}
              </div>
            </PageTitleRow>
            {allocation.header.deliveryDate ? (
              <p className="text-xs per-file-hint font-medium">
                {t("inventory.outwardExitHint", {
                  date: formatDate(allocation.header.deliveryDate),
                })}
              </p>
            ) : (
              <p className="text-xs per-file-hint font-medium">
                {t("inventory.allocationSubtitleReserved")}
              </p>
            )}
          </div>
        )}
      </div>

      {actionError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-700 font-sans">
          {actionError}
        </div>
      ) : null}

      {loading ? (
        <ContentCard bodyClassName="p-6 h-[400px] animate-pulse bg-gray-50/20" />
      ) : error ? (
        <div className="p-12 text-center bg-white border border-(--color-text-border) rounded-2xl max-w-md mx-auto shadow-xs font-sans space-y-4">
          <div className="h-12 w-12 rounded-full bg-rose-50 text-rose-600 grid place-items-center mx-auto">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-foreground">
              {t("validation.allocationNotFound")}
            </h4>
            <p className="text-xs per-file-hint mt-1.5 leading-relaxed">
              {t("validation.allocationNotFoundHint")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center justify-center h-10 px-5 rounded-xl bg-primary text-white font-bold text-xs hover:bg-accent transition cursor-pointer"
          >
            {t("inventory.returnToStockJournal")}
          </button>
        </div>
      ) : (
        <div className="space-y-6 font-sans">
          <ContentCard
            title={t("inventory.orderHeader")}
            bodyClassName="p-5 sm:p-6"
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-5">
              <div>
                <span className={fieldLabelClass}>
                  {t("inventory.salesAndService")}
                </span>
                <span className={fieldValueClass}>
                  {allocation.header.orderType === "Service"
                    ? t("inventory.service")
                    : t("inventory.sales")}
                </span>
              </div>
              <div>
                <span className={fieldLabelClass}>{t("dateTime.date")}</span>
                <span className={fieldValueClass}>
                  {formatDate(allocation.header.date)}
                </span>
              </div>
              <div>
                <span className={fieldLabelClass}>{t("inventory.iofNo")}</span>
                <span className={cn(fieldValueClass, "font-mono")}>
                  {allocation.header.iofNumber || "—"}
                </span>
              </div>
              <div>
                <span className={fieldLabelClass}>
                  {t("inventory.customerName")}
                </span>
                <span className={fieldValueClass}>
                  {allocation.header.customerName}
                </span>
              </div>
              <div>
                <span className={fieldLabelClass}>
                  {t("inventory.projectName")}
                </span>
                <span className={fieldValueClass}>
                  {allocation.header.projectName &&
                  allocation.header.projectName !== "—"
                    ? allocation.header.projectName
                    : "—"}
                </span>
              </div>
              <div>
                <span className={fieldLabelClass}>
                  {t("dateTime.invoiceDate")}
                </span>
                <span className={fieldValueClass}>
                  {formatDate(allocation.header.invoiceDate)}
                </span>
              </div>
              <div>
                <span className={fieldLabelClass}>
                  {t("dateTime.finalDeliveryDate")}
                </span>
                <span className={fieldValueClass}>
                  {formatDate(allocation.header.deliveryDate)}
                </span>
              </div>
              <div>
                <span className={fieldLabelClass}>
                  {t("inventory.dispatchMethod")}
                </span>
                <span className={fieldValueClass}>
                  {allocation.header.dispatchMethod || "—"}
                </span>
              </div>
            </div>
          </ContentCard>

          <ContentCard
            title={t("inventory.productAllocationLines")}
            bodyClassName="p-0 font-sans"
            action={
              showHeaderActions ? (
                <div className="flex flex-wrap items-center gap-2">
                  {deliverableProducts.length > 0 ? (
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={openMarkAsDelivered}
                      className="h-8 px-3 rounded-lg border border-emerald-200 bg-white text-[11px] font-bold text-emerald-700 hover:bg-emerald-50 transition cursor-pointer disabled:opacity-50"
                    >
                      {t("formActions.markAsDelivered")}
                    </button>
                  ) : null}
                  {canRequestRevoke ? (
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => {
                        setActionError("");
                        setRevokeModalOpen(true);
                      }}
                      className="h-8 px-3 rounded-lg border border-rose-200 bg-white text-[11px] font-bold text-rose-600 hover:bg-rose-50 transition cursor-pointer disabled:opacity-50"
                    >
                      {t("formActions.requestRevoke")}
                    </button>
                  ) : null}
                </div>
              ) : null
            }
          >
            <div className="divide-y divide-(--color-text-border)/40">
              {(allocation.products || []).map((product) => (
                <div key={product.id} className="p-4 sm:p-5 space-y-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h4 className="text-base font-extrabold text-foreground">
                          {product.name}
                        </h4>
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border",
                            getStatusBadgeClass(product.status || resolvedStatus)
                          )}
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          {statusLabel(product.status || resolvedStatus)}
                        </span>
                      </div>
                      <span className="text-xs per-file-hint font-medium block mt-1">
                        {[product.partNumber, product.manufacturer]
                          .filter((v) => v && v !== "—")
                          .join(" · ") || "—"}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    <div>
                      <span className={fieldLabelClass}>{t("inventory.iofQty")}</span>
                      <span className={cn(fieldValueClass, "font-mono")}>
                        {product.requestedQty}
                      </span>
                    </div>
                    <div>
                      <span className={fieldLabelClass}>
                        {t("inventory.consideredCostPerUnit")}
                      </span>
                      <span className={cn(fieldValueClass, "font-mono")}>
                        {formatCurrency(product.consideredCostPerUnit)}
                      </span>
                    </div>
                    <div>
                      <span className={fieldLabelClass}>
                        {t("dateTime.deliveryDate")}
                      </span>
                      <span className={fieldValueClass}>
                        {product.deliveryDate
                          ? formatDate(product.deliveryDate)
                          : "—"}
                      </span>
                    </div>
                    <div>
                      <span className={fieldLabelClass}>
                        {t("inventory.invoiceNumber")}
                      </span>
                      <span className={cn(fieldValueClass, "font-mono")}>
                        {product.invoiceNumber || "—"}
                      </span>
                    </div>
                    {product.deliveryNotes ? (
                      <div className="col-span-2 sm:col-span-3 lg:col-span-4">
                        <span className={fieldLabelClass}>
                          {t("inventory.deliveryNotes")}
                        </span>
                        <span className={cn(fieldValueClass, "font-normal whitespace-pre-wrap")}>
                          {product.deliveryNotes}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  <div className="border border-(--color-text-border)/60 rounded-xl overflow-hidden">
                    <ClientTable className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-workspace/40 border-b border-(--color-text-border)/50 font-bold text-[10px] uppercase per-file-hint tracking-wider">
                          
                          <th className="px-4 py-2.5">
                            {t("inventory.batchNumber")}
                          </th>
                          <th className="px-4 py-2.5">
                            {t("inventory.pricePerUnit")}
                          </th>
                          <th className="px-4 py-2.5">
                            {t("inventory.warehouse")}
                          </th>
                          <th className="px-4 py-2.5">
                            {t("inventory.status")}
                          </th>
                          <th className="px-4 py-2.5 text-right">
                            {t("inventory.quantity")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-(--color-text-border)/35">
                        {(product.batches || []).length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-4 py-4 text-center text-gray-400 font-semibold"
                            >
                              {t("inventory.noAvailableBatches")}
                            </td>
                          </tr>
                        ) : (
                          product.batches.map((batch, idx) => (
                            <tr
                              key={`${batch.batchNumber}-${idx}`}
                              className="hover:bg-workspace/10"
                            >
                              <td className="px-4 py-3 font-mono font-bold text-foreground">
                                {batch.batchNumber}
                              </td>
                              <td className="px-4 py-3 font-mono font-semibold text-foreground">
                                {formatCurrency(batch.purchasePricePerUnit)}
                              </td>
                              <td className="px-4 py-3 per-file-hint font-medium">
                                {batch.warehouse}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={cn(
                                    "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border",
                                    getStatusBadgeClass(
                                      batch.status || product.status
                                    )
                                  )}
                                >
                                  {statusLabel(batch.status || product.status)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right font-mono font-bold text-foreground">
                                {batch.allocatedQty}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </ClientTable>
                  </div>
                </div>
              ))}
            </div>
          </ContentCard>
        </div>
      )}
    </AppLayout>
  );
}
